import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from "react-router-dom";
import get from 'lodash/get';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import clone from 'lodash/clone';
import { useAuth } from 'contexts/auth';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import Button from 'components/Button';
import Select from 'components/Select';
import DataTable from 'components/DataTable';
import { withLoading } from 'utils/hoc';
import { DIRECT_MESSAGE_GROUP_NAME, handleErrorResponse, parseJSON } from 'utils/general';
import { updateDatum, getData } from 'api/general';


const ID_FIELD = 'iwatch_account_group_id';
const PAGE_OPTIONS = [10, 20, 50, 100];

const WhatsappSourceAccountGroupListSection = ({
  title,
  title_font_size,
  title_color,
  table_name,
  id_field,
  columns_config,
  column_mapping_config,
  value_mapping_config,
  odd_row_background_color,
  even_row_background_color,
  odd_row_font_color,
  even_row_font_color,
  header_background_color,
  header_font_color,
  page_options,
  extra_params,
  add_data_path,
  edit_data_path,
  addable,
  editable,
  deletable,
  selectable,
  support_pagination,
  show_data_count,
  container_props,
  setLoading,
  ...props
}) => {
  const { setAuth } = useAuth();
  const [rowsData, setRowsData] = useState([]);

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const onToggleExclude = (row) => {
    return updateDatum('/iwatch_account_groups', row.iwatch_account_group_id, {
      excluded_from_feeder: !row.excluded_from_feeder
    }, 'bearer');
  }

  const updateOverride = (id, newValue) => {
    if (newValue === 'null') {
      newValue = null;
    }
    return updateDatum('/iwatch_account_groups', id, {
      monitor_type_override: newValue
    }, 'bearer');
  }

  const [formattedTableColumns, setFormattedTableColumns] = useState([{
    field: 'name',
    label: 'Name',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="start" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem',
          }}
        >
          {data.name === DIRECT_MESSAGE_GROUP_NAME ? 'Ingest Direct Messages (private chats, no group)' : data.name}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'monitor_type_override',
    label: 'Monitor type override',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <Select
          value={data.monitor_type_override || 'null'}
          onChange={(newValue) => {
            updateOverride(data.iwatch_account_group_id, newValue).then((res) => {
              const newDatum = res.data;
              const id = newDatum.iwatch_account_group_id;
              setRowsData((rowList) => {
                let newRowList;
                const index = findIndex(rowList, ['iwatch_account_group_id', id]);
                if (index === -1) {
                  newRowList = rowList;
                } else {
                  newRowList = clone(rowList);
                  newRowList[index] = newDatum;
                }
                return newRowList;
              });
            });
          }}
          options={[{
            label: 'Buy&Sell',
            value: 'null'
          }, {
            label: 'Sell-Only',
            value: 'sell'
          }, {
            label: 'Buy-Only',
            value: 'buy'
          }]}
        />
      </MKBox>;
    }
  }, {
    field: 'excluded_from_feeder',
    label: 'Included in feeder loading?',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <MKTypography
          style={{
            fontSize: '0.875rem'
          }}
        >
          {data.excluded_from_feeder ? 'Excluded' : 'Included'}
        </MKTypography>
      </MKBox>;
    }
  }, {
    field: 'iwatch_account_group_id',
    label: 'Toggle feeder loading status',
    renderContent: (data) => {
      return <MKBox display="flex" justifyContent="center" flexDirection="row" alignItems="center">
        <Button
          size="small"
          variant="gradient"
          onClick={() => {
            onToggleExclude(data).then((res) => {
              const newDatum = res.data;
              const id = newDatum.iwatch_account_group_id;
              setRowsData((rowList) => {
                let newRowList;
                const index = findIndex(rowList, ['iwatch_account_group_id', id]);
                if (index === -1) {
                  newRowList = rowList;
                } else {
                  newRowList = clone(rowList);
                  newRowList[index] = newDatum;
                }
                return newRowList;
              });
            });
          }}
        >
          Toggle
        </Button>
      </MKBox>;
    }
  }]);

  const [searchParams] = useSearchParams();
  const iWatchAccountId = searchParams.get('iwatch_account_id');

  const fetchDataFromApi = useCallback(() => {
    if (!iWatchAccountId) {
      return;
    }
    setLoading(true);
    const dataParams = {
      iwatch_account: iWatchAccountId,
      ...(sortBy ? {
        $orderBy: `${sortBy} ${sortDirection}`,
      } : {}),
      $skip: currentPage * pageSize,
      $top: pageSize,
    };
    return getData('/iwatch_account_groups', dataParams, 'bearer')
      .then(({ data, headers }) => {
        const contentRange = get(headers, 'content-range');
        const size = Number(contentRange.split('/')[1]);
        setRowsData(data);
        setTotalRows(size);
      })
      .catch((err) => {
        handleErrorResponse(err, setAuth);
      }).finally(() => {
        setLoading(false);
      });
  }, [currentPage, pageSize, setAuth, setLoading, sortBy, sortDirection, iWatchAccountId]);

  useEffect(() => {
    fetchDataFromApi();
  }, [fetchDataFromApi]);

  const onClickSort = useCallback((headerName) => {
    const column = find(formattedTableColumns, ['field', headerName]);
    if (column && !column.is_non_sortable) {
      if (sortBy === headerName) {
        setSortDirection((previousSortDirection) => (previousSortDirection === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(headerName);
        setSortDirection('asc');
      }
    }
  }, [formattedTableColumns, sortBy]);

  return <MKBox
    sx={{
      ...parseJSON(container_props, {}),
    }}
    {...props}
  >

    <MKBox mb={2} display="flex" alignItems="center">
      <MKTypography mr="1rem" color={title_color == null ? '#000' : title_color} fontSize="20px">
        Source Feeder Account(s) ({totalRows})
      </MKTypography>
    </MKBox>

    <DataTable
      data={rowsData}
      columns={formattedTableColumns}
      headerBackgroundColor={header_background_color == null ? '#f2f3f7' : header_background_color}
      headerFontColor={header_font_color == null ? '#0094ff' : header_font_color}
      oddRowBackgroundColor={odd_row_background_color == null ? '#eeeeee' : odd_row_background_color}
      oddRowFontColor={odd_row_font_color == null ? '#000' : odd_row_font_color}
      evenRowBackgroundColor={even_row_background_color == null ? '#fff' : even_row_background_color}
      evenRowFontColor={even_row_font_color == null ? '#000' : even_row_font_color}
      idField={ID_FIELD}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={onClickSort}
      paginationDisabled={null}
      onClickRow={null}
      onPressAdd={null}
      onPressEdit={null}
      onPressDelete={null}
      selectable={null}
      currentPage={currentPage}
      onPageChange={setCurrentPage}
      rowsPerPage={pageSize}
      rowsPerPageOptions={PAGE_OPTIONS}
      totalCount={totalRows}
      onRowsPerPageChange={setPageSize}
    />

  </MKBox>;
};

WhatsappSourceAccountGroupListSection.propTypes = {
  title_font_size: PropTypes.string,
  header_color: PropTypes.string,
  text_color: PropTypes.string,
  page_options: PropTypes.string,
  setLoading: PropTypes.func,
  show_header: PropTypes.bool,
  extra_params: PropTypes.object,
  sort_by_options: PropTypes.array,
  currency_options: PropTypes.array,
  source_options: PropTypes.array,
};

WhatsappSourceAccountGroupListSection.defaultProps = {
  title_font_size: '',
  header_color: '#2c589d',
  text_color: '#000',
  page_options: [],
  setLoading: null,
  show_header: false,
  extra_params: {},
  sort_by_options: [],
  currency_options: [],
  source_options: [],
};

export default withLoading(WhatsappSourceAccountGroupListSection);
